import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import * as FortuneWheelIntegration from '../../GameDrawWheel/FortuneWheelIntegration';
import * as LotteryDatabase from '../../GameDrawWheel/GameDrawWheelDatabase';
import * as QuickCashVirtualDeck from './QuickCashVirtualDeck';
import scratchCardService from '../../../../../services/scratchCardService';
import { updatePrizeTableForClaimedPrize } from './updatePrizeTable';
import { 
  Avatar,
  Box, 
  Button, 
  VStack, 
  HStack, 
  Text, 
  Heading, 
  Icon, 
  Alert, 
  AlertIcon, 
  AlertTitle, 
  AlertDescription, 
  useToast, 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  ModalCloseButton,
  useDisclosure, 
  Flex, 
  Stat, 
  StatLabel, 
  StatNumber, 
  StatHelpText, 
  Badge, 
  Progress, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  ListItem,
  UnorderedList,
  OrderedList,
  Center,
  Divider,
  Grid,
  GridItem,
  Tooltip,
  Image
} from '@chakra-ui/react';
import { 
  FaInfoCircle, 
  FaGift, 
  FaCoins, 
  FaTrophy, 
  FaChartLine, 
  FaUserAlt, 
  FaArrowLeft, 
  FaWallet, 
  FaCheck, 
  FaLock,
  FaTicketAlt,
  FaPencilAlt,
  FaPlus,
  FaEye,
  FaMagic,
  FaRedo,
  FaStar
} from 'react-icons/fa';
import ReusableFortuneWheel from '../../../GameUI/ReusableFortuneWheel';
import { QUICK_CASH_CONFIG } from './QuickCashConfig';
import { addPrizeToBalance } from './addPrizeToBalance';
import QuickCashCard from '../../CardTypes/QuickCashCard';
import { QuickCashBountyPool } from '../../BountyPool';
import { useBalance, BalanceProvider } from '../../../../../context/BalanceContext';

/**
 * QuickCashGame Component
 * 
 * A standalone component for the Quick Cash game with complete game logic
 * and blue-themed UI matching the design in the screenshots.
 */
const QuickCashGame = ({ 
  isPreview = false,
  inlineMode = false,
  onBack
}) => {
  // Navigation
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isWinOpen, onOpen: onWinOpen, onClose: onWinClose } = useDisclosure();
  const { isOpen: isLotteryOpen, onOpen: onLotteryOpen, onClose: onLotteryClose } = useDisclosure();
  
  // Access balance from context
  const { balance, tokens, setTokens, addToBalance, subtractFromBalance } = useBalance();
  const [localBalance, setLocalBalance] = useState(balance?.jinu || 1500);
  const userBalance = localBalance;
  
  // Game state
  const [gameState, setGameState] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  
  // Quick Cash specific colors
  const colors = {
    primary: 'blue.900',
    secondary: 'blue.800',
    accent: 'blue.400',
    text: 'white',
    button: 'blue',
    buttonVariant: 'solid',
    progressColor: 'blue.400'
  };
  
  // Function to handle navigation back to all cards
  const handleBackToAllCards = () => {
    if (inlineMode && onBack) {
      // If in inline mode, use the provided onBack function
      onBack();
    } else {
      // Otherwise, navigate to the scratch cards marketplace using the new RESTful URL structure
      navigate('/modular-profile/games/scratch-cards');
    }
  };
  
  // Function to navigate to the game creator
  const handleNavigateToCreator = () => {
    // Check if we're in the modular profile context
    const isModularProfile = window.location.pathname.includes('/modular-profile');
    
    // Create a game based on the current Quick Cash card
    if (isModularProfile) {
      // Navigate to the GameCreationWizard with the quick_cash template
      navigate('/modular-profile/games/scratch-cards/wizard?template=quick_cash');
    } else {
      // Fallback for non-modular profile context
      navigate('/modular-profile/games/scratch-cards/wizard?template=quick_cash');
    }
    
    // Show a toast to confirm navigation
    toast({
      title: "Creating from Quick Cash",
      description: "Loading the Quick Cash template as your starting point...",
      status: "info",
      duration: 2000,
      isClosable: true
    });
  };
  
  // Function to update both local and global balances
  const updateJinuBalance = (amount) => {
    // Ensure amount is a number
    const numericAmount = Number(amount);
    console.log(`Updating balance by ${numericAmount} JINU (original: ${amount})`);
    
    // Update local balance first
    setLocalBalance(prev => {
      const newBalance = prev + numericAmount;
      console.log(`Previous balance: ${prev}, New balance: ${newBalance}`);
      return newBalance;
    });
    
    // Update global balance separately to ensure it's called correctly
    if (numericAmount > 0) {
      // Add to balance - force numeric value
      addPrizeToBalance(numericAmount.toString(), addToBalance, setLocalBalance, toast);
      console.log(`Added ${numericAmount} JINU to global balance context`);
    } else {
      // Subtract from balance - force numeric value
      subtractFromBalance('jinu', Math.abs(numericAmount));
      console.log(`Subtracted ${Math.abs(numericAmount)} JINU from global balance context`);
    }
  };
  
  // Convert prize value string to number (e.g., '5K' to 5000)
  const convertPrizeToNumber = (prizeString) => {
    if (!prizeString) return 0;
    
    console.log(`Converting prize string: ${prizeString}`);
    
    let result = 0;
    if (typeof prizeString === 'string' && prizeString.includes('K')) {
      result = parseInt(prizeString.replace('K', '')) * 1000;
    } else {
      // Handle both string and number inputs
      result = Number(prizeString) || 0;
    }
    
    console.log(`Converted prize value: ${result}`);
    return result;
  };
  
  // Get a random prize based on the prize distribution in the config
  const getRandomPrize = () => {
    if (!QUICK_CASH_CONFIG || !QUICK_CASH_CONFIG.bountyPool || !QUICK_CASH_CONFIG.bountyPool.tiers) {
      // Fallback if config is not properly structured
      return '200';
    }
    
    // Create a weighted array of all prizes based on their count
    const allPrizes = [];
    QUICK_CASH_CONFIG.bountyPool.tiers.forEach(tier => {
      tier.prizes.forEach(prize => {
        // Convert prize amount to display format (e.g., 5000 to '5K')
        const prizeDisplay = prize.amount >= 1000 
          ? `${prize.amount / 1000}K` 
          : prize.amount.toString();
          
        // Add this prize to the array based on its count (weighted random selection)
        for (let i = 0; i < prize.count; i++) {
          allPrizes.push(prizeDisplay);
        }
      });
    });
    
    // Select a random prize from the weighted array
    if (allPrizes.length > 0) {
      const randomIndex = Math.floor(Math.random() * allPrizes.length);
      return allPrizes[randomIndex];
    }
    
    return '200'; // Fallback
  };
  
  // Initialize game state
  const initializeGame = (isWinning = false) => {
    const totalCells = QUICK_CASH_CONFIG.gridSize.rows * QUICK_CASH_CONFIG.gridSize.columns;
    const cellValues = [];
    const winningCells = [];
    
    // Determine if this card is a winner based on the configured probability
    const isWinningCard = isWinning || (Math.random() < QUICK_CASH_CONFIG.winProbability);
    
    // Select prize values based on the configured prize distribution
    const selectedPrizes = [];
    const winCount = QUICK_CASH_CONFIG.winningSymbols || 3;
    
    // This will track which prize value is used for the winning match
    let winningPrizeValue = null;
    
    if (isWinningCard) {
      // For winning cards, select a prize based on the actual prize distribution
      // This ensures the proper odds for each prize tier
      const mainPrize = getRandomPrize();
      selectedPrizes.push(mainPrize);
      
      // Add additional winning symbols with lower values
      for (let i = 1; i < winCount; i++) {
        // For additional winning symbols, use lower prize values
        const lowerPrizes = QUICK_CASH_CONFIG.prizes.filter(prize => {
          const prizeValue = convertPrizeToNumber(prize);
          const mainPrizeValue = convertPrizeToNumber(mainPrize);
          return prizeValue < mainPrizeValue;
        });
        
        if (lowerPrizes.length > 0) {
          const randomIndex = Math.floor(Math.random() * lowerPrizes.length);
          selectedPrizes.push(lowerPrizes[randomIndex]);
        } else {
          // Fallback if no lower prizes available
          selectedPrizes.push(mainPrize);
        }
      }
      
      // Shuffle the prizes to randomize their positions
      selectedPrizes.sort(() => Math.random() - 0.5);
      
      // Randomly select winning cell indices
      while (winningCells.length < winCount) {
        const randomIndex = Math.floor(Math.random() * totalCells);
        if (!winningCells.includes(randomIndex)) {
          winningCells.push(randomIndex);
        }
      }
    } else {
      // For non-winning cards, just use random low-value prizes that don't match
      for (let i = 0; i < winCount; i++) {
        const lowerPrizes = QUICK_CASH_CONFIG.prizes.slice(0, 4); // Use the lowest prize values
        const randomIndex = Math.floor(Math.random() * lowerPrizes.length);
        selectedPrizes.push(lowerPrizes[randomIndex]);
      }
      
      // Make sure prizes don't match by adjusting if needed
      if (selectedPrizes.every(prize => prize === selectedPrizes[0])) {
        if (selectedPrizes.length > 1) {
          selectedPrizes[1] = QUICK_CASH_CONFIG.prizes[0];
        }
        if (selectedPrizes.length > 2) {
          selectedPrizes[2] = QUICK_CASH_CONFIG.prizes[1];
        }
      }
    }
    
    // For winning cards, we need to ensure there are 3 matching values that match one of the winning symbols
    if (isWinningCard) {
      // Choose one of the selected prizes to be the winning match
      winningPrizeValue = selectedPrizes[Math.floor(Math.random() * selectedPrizes.length)];
      
      // Select 3 random positions for the winning value
      const winPositions = [];
      while (winPositions.length < 3) {
        const randomPos = Math.floor(Math.random() * totalCells);
        if (!winPositions.includes(randomPos)) {
          winPositions.push(randomPos);
          // We no longer need to track these as winning cells since we'll use isMatch instead
        }
      }
      
      // Fill the grid with values
      for (let i = 0; i < totalCells; i++) {
        if (winPositions.includes(i)) {
          // Put the winning value in the selected positions
          cellValues.push(winningPrizeValue);
        } else {
          // For non-winning positions, use values that won't create additional matches
          // Avoid using the winning value or creating other 3-of-a-kind matches
          const availablePrizes = QUICK_CASH_CONFIG.prizes.filter(prize => prize !== winningPrizeValue);
          const randomIndex = Math.floor(Math.random() * availablePrizes.length);
          const randomPrize = availablePrizes[randomIndex];
          
          // Check if adding this prize would create an unintended match
          const existingCount = cellValues.filter(value => value === randomPrize).length;
          if (existingCount >= 2) {
            // If this would create an unintended match, use a different prize
            const alternativePrize = availablePrizes[(randomIndex + 1) % availablePrizes.length];
            cellValues.push(alternativePrize);
          } else {
            cellValues.push(randomPrize);
          }
        }
      }
    } else {
      // For non-winning cards, ensure no 3 matching values that match the winning symbols
      // First fill the grid with random values
      
      // Determine lottery ticket value for this non-winning card (1-5 tickets)
      // Get from gameState.cardData if available, otherwise generate based on weighted distribution
      let lotteryTicketValue = 1;
      if (gameState && gameState.cardData && gameState.cardData.lotteryEntries) {
        lotteryTicketValue = gameState.cardData.lotteryEntries;
      } else {
        // Use weighted distribution: 50% for 1, 25% for 2, 15% for 3, 7% for 4, 3% for 5
        const randomValue = Math.random() * 100;
        if (randomValue < 50) {
          lotteryTicketValue = 1;
        } else if (randomValue < 75) {
          lotteryTicketValue = 2;
        } else if (randomValue < 90) {
          lotteryTicketValue = 3;
        } else if (randomValue < 97) {
          lotteryTicketValue = 4;
        } else {
          lotteryTicketValue = 5;
        }
      }
      
      // Add lottery ticket icons to at least 3 cells in non-winning cards
      const ticketPositions = [];
      while (ticketPositions.length < 3) {
        const randomPos = Math.floor(Math.random() * totalCells);
        if (!ticketPositions.includes(randomPos)) {
          ticketPositions.push(randomPos);
        }
      }
      
      for (let i = 0; i < totalCells; i++) {
        if (ticketPositions.includes(i)) {
          // Add lottery ticket icon with the number of tickets
          cellValues.push(`🎟️ x${lotteryTicketValue}`);
        } else {
          const randomPrizeIndex = Math.floor(Math.random() * QUICK_CASH_CONFIG.prizes.length);
          const randomPrize = QUICK_CASH_CONFIG.prizes[randomPrizeIndex];
          
          // Check if adding this prize would create a match
          const existingCount = cellValues.filter(value => value === randomPrize).length;
          if (existingCount >= 2) {
            // If this would create a match, use a different prize
            const alternativePrize = QUICK_CASH_CONFIG.prizes[(randomPrizeIndex + 1) % QUICK_CASH_CONFIG.prizes.length];
            cellValues.push(alternativePrize);
          } else {
            cellValues.push(randomPrize);
          }
        }
      }
      
      // Verify no unintended matches
      for (const prize of selectedPrizes) {
        const count = cellValues.filter(value => value === prize).length;
        if (count >= 3) {
          // If we accidentally created a match, replace one occurrence
          const indexToReplace = cellValues.findIndex(value => value === prize);
          if (indexToReplace !== -1) {
            // Replace with a different prize
            const alternativePrizes = QUICK_CASH_CONFIG.prizes.filter(p => p !== prize);
            cellValues[indexToReplace] = alternativePrizes[Math.floor(Math.random() * alternativePrizes.length)];
          }
        }
      }
    }
    
    // Calculate win amount based on the matching prize value
    let winAmount = 0;
    if (isWinningCard && winningPrizeValue) {
      // The win amount is the value of the matching prize
      winAmount = convertPrizeToNumber(winningPrizeValue);
    }
    
    // Create the game state
    return {
      status: 'active',
      cellValues,
      winningCells,
      revealedCells: [],
      isWinningCard,
      winAmount,
      winningSymbolsRevealed: false, // Initially, winning symbols are not revealed
      prizeValues: selectedPrizes, // Store the selected prize values for this card
      winningPrizeValue // Store the prize value that creates the winning match
    };
  };
  
  // Handle buying cards - supports multiple card purchases
  const handleBuy = (cardCount = 1) => {
    // Calculate cost with potential discount
    let discount = 0;
    if (cardCount >= 10) discount = 10;
    else if (cardCount >= 5) discount = 5;
    
    const totalCost = QUICK_CASH_CONFIG.cost * cardCount;
    const discountedCost = totalCost * (100 - discount) / 100;
    
    // Get the latest balance from the context to ensure we're using the most up-to-date value
    const currentBalance = balance?.jinu || 0;
    console.log(`Current balance: ${currentBalance}, Cost: ${discountedCost}`);
    
    // Check if user has enough balance
    if (currentBalance < discountedCost) {
      toast({
        title: "Insufficient balance",
        description: `You need ${discountedCost} JINU to buy ${cardCount} card${cardCount > 1 ? 's' : ''}. Your current balance is ${currentBalance} JINU.`,
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    // Deduct the cost from user balance
    updateJinuBalance(-discountedCost);
    
    // If buying multiple cards, store them for later play
    if (cardCount > 1) {
      const cards = [];
      for (let i = 0; i < cardCount; i++) {
        // Draw the next card from the batch
        const drawnCard = QuickCashVirtualDeck.drawNextCard();
        cards.push(drawnCard);
      }
      
      // Store cards in local storage for later play
      const storedCards = JSON.parse(localStorage.getItem('quickcash_purchased_cards') || '[]');
      localStorage.setItem('quickcash_purchased_cards', JSON.stringify([...storedCards, ...cards]));
      
      toast({
        title: `${cardCount} Cards Purchased`,
        description: `You've purchased ${cardCount} cards. Play them anytime from your inventory!`,
        status: "success",
        duration: 3000,
        isClosable: true
      });
      
      // For now, just play the first card
      playCard(cards[0]);
    } else {
      // Draw the next card from the batch
      const drawnCard = QuickCashVirtualDeck.drawNextCard();
      console.log('Drew new card:', drawnCard);
      
      // Play the single card immediately
      playCard(drawnCard);
    }
  };
  
  // Play a specific card
  const playCard = (drawnCard) => {
    console.log('Playing card:', drawnCard);
    
    // First, completely clear the current game state to ensure a fresh start
    setGameState(null);
    setGameResult(null);
    
    // Small delay to ensure the component fully unmounts and remounts
    setTimeout(() => {
      // Create a fresh game state based on the drawn card
      const newGameState = QuickCashVirtualDeck.initializeGameFromCard(drawnCard);
      
      // Get batch statistics for UI display
      const batchStats = QuickCashVirtualDeck.getBatchStatistics();
      
      // Enhance the game state with batch information
      newGameState.currentBatch = batchStats.batchNumber;
      newGameState.batchSize = batchStats.totalCards;
      newGameState.gamesPlayed = batchStats.cardsDrawn;
      
      // Store the current card data for verification and debugging
      newGameState.cardData = drawnCard;
      
      // Store the prize values for this card
      newGameState.prizeValues = drawnCard.winningSymbols;
      
      // If this is a winning card, store the winning prize value
      if (drawnCard.isWinner && drawnCard.prizeAmount > 0) {
        console.log('This is a winning card with prize amount:', drawnCard.prizeAmount);
        // First winning symbol is typically the prize value
        newGameState.winningPrizeValue = drawnCard.winningSymbols[0];
      }
      
      // Ensure the card starts with the correct initial state
      newGameState.winningSymbolsRevealed = false;
      newGameState.status = 'active';
      newGameState.revealedCells = [];
      newGameState.uniqueId = 'card-' + Date.now(); // Add a unique ID to force re-render
      
      // Set the game state once with all properties properly initialized
      setGameState(newGameState);
      
      // Close any open modals
      if (isWinOpen) {
        onWinClose();
      }
      if (isLotteryOpen) {
        onLotteryClose();
      }
      
      // Reset game result
      setGameResult(null);
      
      // Card Ready notification removed - no toast message needed
    }, 50); // Short delay to ensure clean state transition
    
    // Console log for debugging
    console.log('Playing card:', drawnCard);
    // Get batch statistics for logging
    const currentBatchStats = QuickCashVirtualDeck.getBatchStatistics();
    console.log('Batch statistics:', currentBatchStats);
  };
  
  // Check for winning combinations in the revealed cells
  const checkForWinningCombination = () => {
    if (!gameState || !gameState.cellValues) {
      console.error('❌ Cannot check for winning combinations: gameState or cellValues is missing');
      return null;
    }
    
    console.log('🔍 Checking for winning combination with revealed cells:', gameState.revealedCells);
    console.log('🎮 Cell values:', gameState.cellValues);
    
    // CRITICAL FIX: If revealed cells array is empty but we're in the handleRevealAll function,
    // create a temporary array with all cells for checking
    let cellsToCheck = gameState.revealedCells;
    if (!cellsToCheck || cellsToCheck.length === 0) {
      console.warn('⚠️ revealedCells array is empty! Creating temporary array for checking');
      const totalCells = QUICK_CASH_CONFIG.gridSize.rows * QUICK_CASH_CONFIG.gridSize.columns;
      cellsToCheck = Array.from({ length: totalCells }, (_, i) => i);
    }
    
    // If this card is predetermined to be a winner, check if we've revealed the winning cells
    if (gameState.isWinningCard && gameState.winningCells && gameState.winningCells.length > 0) {
      // Check if all winning cells are revealed
      const allWinningCellsRevealed = gameState.winningCells.every(index => 
        cellsToCheck.includes(index)
      );
      
      if (allWinningCellsRevealed) {
        // If all winning cells are revealed, return the winning prize value
        console.log('✅ All winning cells revealed! Prize:', gameState.winAmount);
        return gameState.cellValues[gameState.winningCells[0]]; // Return the value of the first winning cell
      }
    }
    
    // Count occurrences of each value in the revealed cells
    const valueCounts = {};
    cellsToCheck.forEach(index => {
      if (index >= 0 && index < gameState.cellValues.length) {
        const value = gameState.cellValues[index];
        if (value) { // Only count non-empty values
          valueCounts[value] = (valueCounts[value] || 0) + 1;
        }
      }
    });
    
    console.log('🔢 Value counts:', valueCounts);
    
    // Debug: Log all values that appear 3 or more times
    Object.entries(valueCounts).forEach(([value, count]) => {
      if (count >= 3) {
        console.log(`🎯 Found potential match: ${value} appears ${count} times`);
      }
    });
    
    // Check if any value appears 3 or more times
    for (const [value, count] of Object.entries(valueCounts)) {
      if (count >= 3) {
        console.log(`Found 3 or more matches for value: ${value}`);
        // Check if this value is a prize
        const isPrize = gameState.prizeValues && gameState.prizeValues.includes(value);
        if (isPrize) {
          console.log(`Value ${value} is a prize!`);
          return value;
        }
      }
    }
    
    return null;
  };
  
  // Handle revealing winning symbols first, then individual cells
  const handleReveal = (index) => {
    if (!gameState || gameState.status !== 'active') return;
    
    // Create a copy of the current game state
    const newState = { ...gameState };
    
    // If winning symbols haven't been revealed yet, reveal them first
    if (!newState.winningSymbolsRevealed) {
      newState.winningSymbolsRevealed = true;
      
      // Determine the potential win amount based on the prize values
      if (newState.isWinningCard && newState.prizeValues && newState.prizeValues.length > 0) {
        // Select the highest prize value for this winning card
        const highestPrize = newState.prizeValues.reduce((max, prize) => {
          const prizeValue = convertPrizeToNumber(prize);
          return prizeValue > max ? prizeValue : max;
        }, 0);
        
        // Set the potential win amount
        newState.potentialWinAmount = highestPrize;
      }
      
      setGameState(newState);
      // Winning Symbols Revealed notification removed - no toast message needed
      
      return; // Exit early, wait for next click to reveal cells
    }
    
    // Add this cell to the revealed cells if it's not already there
    if (!newState.revealedCells.includes(index)) {
      newState.revealedCells.push(index);
    }
    
    // Check if we have a winning combination
    const matchedValue = checkForWinningCombination();
    
    if (matchedValue) {
      newState.status = 'won';
      
      // Get the win amount from the matched value
      const winAmount = convertPrizeToNumber(matchedValue);
      newState.winAmount = winAmount;
      
      // DIRECT BALANCE UPDATE: Add the prize to the balance immediately
      // This bypasses the updateJinuBalance function which might have issues
      console.log(`🏆 WINNING! Adding ${winAmount} JINU directly to balance`);
      
      // Force numeric conversion and directly update both balances
      const numericWinAmount = Number(winAmount);
      
      // Use the dedicated function that handles both local and global balance updates
      addPrizeToBalance(matchedValue, addToBalance, setLocalBalance, toast);
      // Prize Won notification removed - no toast message needed
      
      // Set game result for modal
      setGameResult({
        matchedValue,
        amount: winAmount
      });
      
      // Show win modal
      onWinOpen();
    }
    
    // Check if all cells have been revealed
    const totalCells = QUICK_CASH_CONFIG.gridSize.rows * QUICK_CASH_CONFIG.gridSize.columns;
    if (newState.revealedCells.length === totalCells) {
      // All cells revealed, game is complete
      if (newState.status === 'active') {
        // If the game is still active (not won), mark it as lost
        newState.status = 'lost';
        
        // Generate a mock user ID - in production, use the actual user ID
        const mockUserId = "user_" + Math.random().toString(36).substring(2, 9);
        const batchId = gameState.currentBatch?.toString() || "1";
        
        // Get the lottery tickets value from the card data in the virtual deck
        // This ensures we use the predetermined lottery ticket value
        const ticketValue = gameState.cardData && gameState.cardData.lotteryEntries ? 
          gameState.cardData.lotteryEntries : 
          Math.floor(Math.random() * 5) + 1; // Fallback to random if not available
        
        // Add lottery tickets to the database
        FortuneWheelIntegration.addLotteryTicketsForCard(
          mockUserId, 
          batchId, 
          gameState.cardId || `card_${Date.now()}`, // Use the card ID from batch system if available
          ticketValue
        );
        
        // Update lottery tickets count in game state
        newState.lotteryTickets = (newState.lotteryTickets || 0) + ticketValue;
        
        // Update win probability
        newState.winProbability = FortuneWheelIntegration.getUserWinProbability(mockUserId, batchId);
        
        // Get batch statistics for display
        const batchStats = QuickCashVirtualDeck.getBatchStatistics();
        console.log('Batch statistics after reveal:', batchStats);
        
        // No toast message for non-winning cards - lottery tickets will be shown in the grid
      }
    }
    
    // Update the game state
    setGameState(newState);
    
    // We've removed the automatic reset timer to give the user control
    // The user will now need to click the Buy Card button to get a new card
  };
  
  // Define confetti animation keyframes
  const confettiFall = keyframes`
    0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(500px) rotate(360deg); opacity: 0; }
  `;
  
  // Define shimmer animation for the prize text
  const shimmer = keyframes`
    0% { background-position: -100% 0; }
    100% { background-position: 200% 0; }
  `;
  
  // Auto-close win modal after a delay
  useEffect(() => {
    if (isWinOpen) {
      const timer = setTimeout(() => {
        onWinClose();
      }, 5000); // Auto close after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [isWinOpen, onWinClose]);

  // Handle revealing all cells at once
  const handleRevealAll = () => {
    if (!gameState || gameState.status !== 'active') return;
    
    console.log('🔍 handleRevealAll called with gameState:', gameState);
    
    // Create a copy of the current game state
    const newState = { ...gameState };
    
    // If winning symbols haven't been revealed yet, reveal them first
    if (!newState.winningSymbolsRevealed) {
      newState.winningSymbolsRevealed = true;
      setGameState(newState);
      // Winning Symbols Revealed notification removed - no toast message needed
      
      return; // Exit early, wait for next click to reveal all cells
    }
    
    // Reveal all cells
    const totalCells = QUICK_CASH_CONFIG.gridSize.rows * QUICK_CASH_CONFIG.gridSize.columns;
    newState.revealedCells = Array.from({ length: totalCells }, (_, i) => i);
    
    // IMPORTANT: Update the game state BEFORE checking for winning combinations
    console.log('📢 Setting revealed cells:', newState.revealedCells);
    setGameState(newState);
    
    // Check if we have a winning combination - use the updated state directly
    console.log('🔍 Checking for winning combinations with all cells revealed');
    const matchedValue = checkForWinningCombination();
    
    if (matchedValue) {
      newState.status = 'won';
      
      // Get the win amount from the matched value
      const winAmount = convertPrizeToNumber(matchedValue);
      newState.winAmount = winAmount;
      
      // DIRECT BALANCE UPDATE: Add the prize to the balance immediately
      // This bypasses the updateJinuBalance function which might have issues
      console.log(`🏆 WINNING! Adding ${winAmount} JINU directly to balance`);
      
      // Force numeric conversion and directly update both balances
      const numericWinAmount = Number(winAmount);
      
      // Use the dedicated function that handles both local and global balance updates
      addPrizeToBalance(matchedValue, addToBalance, setLocalBalance, toast);
      // Prize Won notification removed - no toast message needed
      
      // Set game result for modal
      setGameResult({
        matchedValue,
        amount: winAmount
      });
      
      // Show win modal
      onWinOpen();
    } else {
      // No winning combination, game is lost
      newState.status = 'lost';
      
      // Generate a mock user ID - in production, use the actual user ID
      const mockUserId = "user_" + Math.random().toString(36).substring(2, 9);
      const batchId = gameState.currentBatch?.toString() || "1";
      
      // Determine ticket value based on card type (1-5 tickets)
      // For this example, we'll use a random value between 1 and 5
      const ticketValue = Math.floor(Math.random() * 5) + 1;
      
      // Add lottery tickets to the database
      FortuneWheelIntegration.addLotteryTicketsForCard(
        mockUserId, 
        batchId, 
        gameState.cardId || `card_${Date.now()}`, // Use the card ID from batch system if available
        ticketValue
      );
      
      // Update lottery tickets count in game state
      newState.lotteryTickets = (newState.lotteryTickets || 0) + ticketValue;
      
      // Update win probability
      newState.winProbability = FortuneWheelIntegration.getUserWinProbability(mockUserId, batchId);
      
      // Get batch statistics for display
      const batchStats = QuickCashVirtualDeck.getBatchStatistics();
      console.log('Batch statistics after reveal all:', batchStats);
      
      toast({
        title: "Better luck next time!",
        description: `You earned ${ticketValue} lottery tickets for the Game Draw Wheel!`,
        status: "info",
        duration: 3000,
        isClosable: true
      });
    }
    
    // Update the game state
    setGameState(newState);
    
    // We've removed the automatic reset timer to give the user control
    // The user will now need to click the Buy Card button to get a new card
  };
  
  // Handle playing again
  const handlePlayAgain = () => {
    // Clear the current game state before buying a new card
    setGameState(null);
    setGameResult(null);
    
    // Draw a new card with a slight delay to ensure clean state
    setTimeout(() => {
      handleBuy();
    }, 100);
  };
  
  // Handle sending as gift
  const handleGift = () => {
    // Implementation for gifting functionality
    toast({
      title: "Coming Soon",
      description: "Gifting functionality will be available soon!",
      status: "info",
      duration: 3000,
      isClosable: true
    });
  };
  
  // Handle minting a card as an NFT
  const handleMintCard = (revealed = false) => {
    if (!gameState) {
      toast({
        title: "No Active Card",
        description: "You need to have an active card to mint.",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    // Check if user has enough balance
    if (userBalance < QUICK_CASH_CONFIG.cost) {
      toast({
        title: "No Active Card",
        description: "You need to have an active card to mint.",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    // For unrevealed cards, check that it's not already revealed
    if (!revealed && gameState.revealedCells.length > 0) {
      toast({
        title: "Card Already Revealed",
        description: "You can only mint unrevealed cards that haven't been played yet.",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    // For revealed cards, check that it's fully revealed
    if (revealed && gameState.revealedCells.length < 9) {
      toast({
        title: "Card Not Fully Revealed",
        description: "You need to fully reveal the card before minting it as a revealed NFT.",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    // Prepare NFT metadata
    const nftMetadata = {
      id: gameState.cardId,
      batchId: gameState.batchId,
      batchNumber: gameState.batchNumber,
      cardNumber: gameState.cardNumber,
      deckHash: gameState.deckHash || 'unknown',
      revealed: revealed,
      isWinner: revealed ? gameState.isWinningCard : null,
      prizeAmount: revealed ? gameState.winAmount : null,
      mintedAt: Date.now(),
      game: 'Quick Cash',
      gameConfig: QUICK_CASH_CONFIG.id
    };
    
    // In a real implementation, this would call a blockchain function to mint the NFT
    // For now, we'll just simulate it
    
    // Store minted cards in local storage
    const mintedCards = JSON.parse(localStorage.getItem('quickcash_minted_cards') || '[]');
    mintedCards.push(nftMetadata);
    localStorage.setItem('quickcash_minted_cards', JSON.stringify(mintedCards));
    
    toast({
      title: `Card Minted as ${revealed ? 'Revealed' : 'Unrevealed'} NFT`,
      description: revealed 
        ? `Your ${gameState.isWinningCard ? 'winning' : 'non-winning'} card has been added to your collection.`
        : `Your unrevealed card has been minted and can be gifted to others.`,
      status: "success",
      duration: 5000,
      isClosable: true
    });
    
    // If this was an unrevealed card mint, we should create a new game
    if (!revealed) {
      // Reset game state after a short delay
      setTimeout(() => {
        setGameState(null);
      }, 1000);
    }
  };
  
  // Handle revealing winning symbols
  const handleRevealWinningSymbols = () => {
    if (!gameState) return;
    
    // Update game state to show winning symbols
    setGameState(prevState => ({
      ...prevState,
      winningSymbolsRevealed: true
    }));
    
    // Winning Symbols Revealed notification removed - no toast message needed
  };

  // Game controls section with enhanced options
  const renderGameControls = () => {
    // Define card purchase options
    const purchaseOptions = [
      { count: 1, label: 'Buy 1 Card', discount: 0 },
      { count: 5, label: 'Buy 5 Cards', discount: 5 },
      { count: 10, label: 'Buy 10 Cards', discount: 10 }
    ];
    
    return (
      <VStack spacing={4} align="stretch" w="100%" mb={4}>
        <Heading size="md" color="white">Game Controls</Heading>
        
        {/* Card Purchase Options - Only show when no active game */}
        {!gameState && (
          <Box borderWidth="1px" borderRadius="lg" p={4} bg="rgba(0,0,0,0.2)">
            <Heading size="sm" mb={3} color="white">Purchase Options</Heading>
            <SimpleGrid columns={[1, 3]} spacing={3}>
              {purchaseOptions.map(option => {
                const totalCost = QUICK_CASH_CONFIG.cost * option.count;
                const discountedCost = totalCost * (100 - option.discount) / 100;
                
                return (
                  <Button 
                    key={option.count}
                    colorScheme="blue" 
                    size="md" 
                    leftIcon={<Icon as={FaCoins} />}
                    onClick={() => handleBuy(option.count)}
                    isDisabled={userBalance < discountedCost}
                    height="auto"
                    py={2}
                  >
                    <VStack spacing={0}>
                      <Text>{option.label}</Text>
                      <Text fontSize="sm">{discountedCost} JINU</Text>
                      {option.discount > 0 && (
                        <Badge colorScheme="green">{option.discount}% OFF</Badge>
                      )}
                    </VStack>
                  </Button>
                );
              })}
            </SimpleGrid>
          </Box>
        )}
        
        {/* Active Game Controls */}
        {gameState && (
          <Box borderWidth="1px" borderRadius="lg" p={4} bg="rgba(0,0,0,0.2)">
            <Heading size="sm" mb={3} color="white">Game Actions</Heading>
            <SimpleGrid columns={[1, 2]} spacing={3}>
              {!gameState.winningSymbolsRevealed && (
                <Button 
                  colorScheme="teal" 
                  size="md"
                  leftIcon={<Icon as={FaEye} />}
                  onClick={handleRevealWinningSymbols}
                >
                  Reveal
                </Button>
              )}
              
              {gameState.winningSymbolsRevealed && gameState.revealedCells.length < 9 && (
                <Button 
                  colorScheme="purple" 
                  size="md"
                  leftIcon={<Icon as={FaMagic} />}
                  onClick={handleRevealAll}
                >
                  Reveal
                </Button>
              )}
              
              {(gameState.revealedCells.length === 9 || gameState.status === 'won' || gameState.status === 'lost') && (
                <Button 
                  colorScheme="blue" 
                  size="md"
                  leftIcon={<Icon as={FaRedo} />}
                  onClick={() => {
                    // Reset game state first
                    setGameState(null);
                    // Then buy a new card
                    setTimeout(() => handleBuy(1), 100);
                  }}
                >
                  Play Again
                </Button>
              )}
              
              {/* Single Mint button that handles both revealed and unrevealed cards */}
              <Button 
                colorScheme="green" 
                size="md"
                leftIcon={<Icon as={FaStar} />}
                onClick={() => {
                  // If card is fully revealed, mint as revealed, otherwise mint as unrevealed
                  const isRevealed = gameState.revealedCells.length === 9;
                  handleMintCard(isRevealed);
                }}
              >
                Mint
              </Button>
            </SimpleGrid>
          </Box>
        )}
        
        {/* Inventory Section - Show purchased cards */}
        <Box borderWidth="1px" borderRadius="lg" p={4} bg="rgba(0,0,0,0.2)">
          <Heading size="sm" mb={3} color="white">Card Inventory</Heading>
          <HStack>
            <Button 
              colorScheme="purple"
              size="sm"
              leftIcon={<Icon as={FaTicketAlt} />}
              onClick={() => toast({
                title: "Coming Soon",
                description: "Card inventory feature will be available soon!",
                status: "info",
                duration: 3000,
                isClosable: true
              })}
            >
              Played Cards
            </Button>
            <Button 
              colorScheme="green"
              size="sm"
              leftIcon={<Icon as={FaStar} />}
              onClick={() => toast({
                title: "Coming Soon",
                description: "NFT collection feature will be available soon!",
                status: "info",
                duration: 3000,
                isClosable: true
              })}
            >
              NFT Collection
            </Button>
          </HStack>
        </Box>
      </VStack>
    );
  };
  
  // Get bounty pool data
  const getBountyPoolData = () => {
    return {
      totalCards: 10000,
      cardsMinted: 2500,
      cardsRemaining: 7500,
      totalPrizePool: 1500000,
      claimedPrizes: 375000,
      remainingPrizes: 1125000,
      topPrizes: {
        total: 12,
        claimed: 7,
        remaining: 5
      },
      recentWinners: [
        { player: '0x7a2...3f1e', amount: 50000, timestamp: Date.now() - 1000 * 60 * 2, type: '50K' },
        { player: '0x3b9...8d7c', amount: 1000, timestamp: Date.now() - 1000 * 60 * 5, type: '1K' },
        { player: '0x6f2...4e8d', amount: 0, timestamp: Date.now() - 1000 * 60 * 12, type: 'GIFT BOX' },
        { player: '0x9c4...2b1d', amount: 500, timestamp: Date.now() - 1000 * 60 * 18, type: '500' },
        { player: '0x5e7...9d3f', amount: 10000, timestamp: Date.now() - 1000 * 60 * 25, type: '10K' },
      ],
      prizeSummary: {
        topPrizes: { total: 6, claimed: 1 },
        midPrizes: { total: 230, claimed: 80 },
        lowPrizes: { total: 16150, claimed: 5763 },
        specialRewards: { total: 32614, claimed: 6639 }
      }
    };
  };
  
  // Initialize the game on component mount
  useEffect(() => {
    // Recreate the virtual deck to ensure lottery tickets are properly included
    console.log('Recreating the virtual deck with lottery tickets');
    QuickCashVirtualDeck.resetVirtualDeck(10000, 100);
    
    // Log the batch statistics to verify lottery tickets distribution
    const batchStats = QuickCashVirtualDeck.getBatchStatistics();
    console.log('Batch statistics after recreation:', batchStats);
    
    if (!gameState) {
      // Initialize with a default state
      initializeGame();
    }
  }, []);

  // Initialize batch system on component mount
  useEffect(() => {
    console.log('Initializing Quick Cash game...');
    
    // Always ensure we have a batch available
    try {
      let batch = QuickCashVirtualDeck.getCurrentBatch();
      if (!batch) {
        // Create a new batch with the segmented approach
        batch = QuickCashVirtualDeck.createNewBatch(10000, 100);
        console.log('Created new batch with segmented approach');
      } else {
        console.log('Using existing batch:', batch.batchNumber);
      }
      
      // Important: Set gameState to null initially to ensure purchase options are shown
      // This fixes the issue where game controls aren't displaying
      setGameState(null);
      
    } catch (error) {
      console.error('Error initializing batch:', error);
      // Create a new batch if there was an error
      QuickCashVirtualDeck.createNewBatch(10000, 100);
      setGameState(null);
    }
    
    // Only auto-initialize game state in preview mode
    if (isPreview) {
      const initialState = initializeGame(false);
      const mockUserId = "user_" + Math.random().toString(36).substring(2, 9);
      const enhancedGameState = FortuneWheelIntegration.getEnhancedGameState(initialState, mockUserId);
      setGameState(enhancedGameState);
    }
    
    // Cleanup function to prevent memory leaks and state issues
    return () => {
      // Reset game state when component unmounts
      setGameState(null);
      setGameResult(null);
    };
  }, []);

  return (
    <Box width="100%" minHeight="auto" p={4}>
      <Flex direction={{ base: 'column', md: 'row' }} width="100%" gap={3}>
        {/* Card - Left side */}
        <Box width={{ base: "100%", md: "400px" }}>
          <QuickCashCard 
            key={gameState?.cardData?.id || 'card-' + Math.random()}
            isPreview={isPreview}
            gameState={gameState}
            onReveal={handleReveal}
          />
        </Box>
        
        {/* Game Controls - Middle column, blue themed */}
        <Box width={{ base: "100%", md: "400px" }} mx={{ base: 0, md: 4 }}>
          <Box 
            bg="rgba(0, 15, 30, 0.95)" 
            p={5} 
            borderRadius="lg"
            width="100%"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(0, 128, 255, 0.15)"
            backdropFilter="blur(10px)"
            height="fit-content"
          >
            <VStack spacing={4} align="stretch">
              {/* Game Controls */}
              <VStack spacing={4} align="stretch" w="100%" mb={4}>
                {/* Simple Game Controls */}
                <VStack spacing={4} align="stretch">
                  {/* Main action button - changes based on game state */}
                  <Box borderWidth="1px" borderRadius="lg" p={4} bg="rgba(0,0,0,0.2)">
                    {/* Dynamic main button */}
                    {!gameState ? (
                      // Buy Card button - initial state
                      <Button
                        colorScheme="blue"
                        size="lg"
                        leftIcon={<Icon as={FaCoins} />}
                        onClick={() => handleBuy(1)}
                        width="100%"
                        height="60px"
                        isDisabled={userBalance < QUICK_CASH_CONFIG.cost}
                      >
                        Buy Card ({QUICK_CASH_CONFIG.cost} JINU)
                      </Button>
                    ) : !gameState.winningSymbolsRevealed ? (
                      // Reveal Winning Symbols button
                      <Button
                        colorScheme="teal"
                        size="lg"
                        leftIcon={<Icon as={FaEye} />}
                        onClick={handleRevealWinningSymbols}
                        width="100%"
                        height="60px"
                      >
                        Reveal Winning Symbols
                      </Button>
                    ) : gameState.revealedCells.length < 9 ? (
                      // Reveal All Cells button
                      <Button
                        colorScheme="purple"
                        size="lg"
                        leftIcon={<Icon as={FaMagic} />}
                        onClick={handleRevealAll}
                        width="100%"
                        height="60px"
                      >
                        Reveal All Cells
                      </Button>
                    ) : (
                      // Buy Card button again (after game is complete)
                      <Button
                        colorScheme="blue"
                        size="lg"
                        leftIcon={<Icon as={FaCoins} />}
                        onClick={() => {
                          // Reset the game state first
                          setGameState(null);
                          // Use handleBuy which properly handles cost deduction and drawing from the virtual deck
                          handleBuy(1);
                        }}
                        width="100%"
                        height="60px"
                        isDisabled={userBalance < QUICK_CASH_CONFIG.cost}
                      >
                        Buy Card ({QUICK_CASH_CONFIG.cost} JINU)
                      </Button>
                    )}
                  </Box>
                  
                  {/* Mint button - always visible but disabled when no game */}
                  <Box borderWidth="1px" borderRadius="lg" p={4} bg="rgba(0,0,0,0.2)">
                    <Button
                      colorScheme="green"
                      size="md"
                      leftIcon={<Icon as={FaStar} />}
                      onClick={() => {
                        if (gameState) {
                          // If card is fully revealed, mint as revealed, otherwise mint as unrevealed
                          const isRevealed = gameState.revealedCells.length === 9;
                          handleMintCard(isRevealed);
                        }
                      }}
                      width="100%"
                      isDisabled={!gameState}
                    >
                      Mint NFT
                    </Button>
                  </Box>
                  
                  {/* Multiple purchase options */}
                  <SimpleGrid columns={2} spacing={3}>
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      size="md"
                      leftIcon={<Icon as={FaCoins} />}
                      onClick={() => handleBuy(5)}
                      isDisabled={userBalance < QUICK_CASH_CONFIG.cost * 5}
                    >
                      Buy x5
                    </Button>
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      size="md"
                      leftIcon={<Icon as={FaCoins} />}
                      onClick={() => handleBuy(10)}
                      isDisabled={userBalance < QUICK_CASH_CONFIG.cost * 10}
                    >
                      Buy 10 Random
                    </Button>
                  </SimpleGrid>
                </VStack>
                
                {/* Inventory Section - Show purchased cards */}
                <Box borderWidth="1px" borderRadius="lg" p={4} bg="rgba(0,0,0,0.2)">
                  <Heading size="sm" mb={3} color="white">Card Inventory</Heading>
                  <HStack>
                    <Button 
                      colorScheme="purple"
                      size="sm"
                      leftIcon={<Icon as={FaTicketAlt} />}
                      onClick={() => toast({
                        title: "Coming Soon",
                        description: "Card inventory feature will be available soon!",
                        status: "info",
                        duration: 3000,
                        isClosable: true
                      })}
                    >
                      Played Cards
                    </Button>
                    <Button 
                      colorScheme="green"
                      size="sm"
                      leftIcon={<Icon as={FaStar} />}
                      onClick={() => toast({
                        title: "Coming Soon",
                        description: "NFT collection feature will be available soon!",
                        status: "info",
                        duration: 3000,
                        isClosable: true
                      })}
                    >
                      NFT Collection
                    </Button>
                  </HStack>
                </Box>
              </VStack>
              
              <Box bg="blackAlpha.400" p={3} borderRadius="md">
                <HStack mb={2}>
                  <Icon as={FaInfoCircle} color={colors.accent} />
                  <Heading size="sm" color="white">How to Play:</Heading>
                </HStack>
                <Text color="white" fontSize="sm">
                  Click on each square to reveal symbols. Match 3 of the
                  winning symbols shown above to win their prize value.
                </Text>
              </Box>
            </VStack>
          </Box>
        </Box>
        
        {/* Create Game Box - Right side, blue themed */}
        <Box width={{ base: "100%", md: "400px" }}>
          <Box 
            bg="rgba(0, 15, 30, 0.95)" 
            p={5} 
            borderRadius="lg"
            mb={4}
            width="100%"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(0, 128, 255, 0.15)"
            backdropFilter="blur(10px)"
          >
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="#3182CE" mb={2}>Create Your Game</Heading>
              
              <Text color="gray.300" fontSize="sm" mb={2}>
                Create your own custom Quick Cash scratch card with your own prize structure, odds, and design.
              </Text>
              
              <Box>
                <Text color="gray.300" mb={2}>Starting from the Quick Cash template, you can:</Text>
                <VStack align="start" spacing={2} pl={4}>
                  <HStack>
                    <Icon as={FaCheck} color="green.400" />
                    <Text color="gray.300">Customize card appearance</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaCheck} color="green.400" />
                    <Text color="gray.300">Set your own prize structure</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaCheck} color="green.400" />
                    <Text color="gray.300">Define win probabilities</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaCheck} color="green.400" />
                    <Text color="gray.300">Lock prize funds in escrow</Text>
                  </HStack>
                </VStack>
              </Box>
              
              <Button
                leftIcon={<Icon as={FaPencilAlt} />}
                colorScheme="blue"
                size="lg"
                onClick={handleNavigateToCreator}
                mt={2}
              >
                Create Custom Game
              </Button>
              
              <Text color="gray.500" fontSize="xs" mt={2} textAlign="center">
                Your game will be published to the PalmHeist marketplace after review
              </Text>
            </VStack>
          </Box>
          

        </Box>
      </Flex>
      
      {/* Bounty Pool Section */}
      <Box width="100%" mt={6}>
        <Box 
          bg="rgba(0, 15, 30, 0.95)" 
          p={5} 
          borderRadius="lg"
          boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
          border="1px solid rgba(0, 128, 255, 0.15)"
          backdropFilter="blur(10px)"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 50, 100, 0.1) 0%, rgba(0, 20, 40, 0.1) 100%)',
            zIndex: 0,
          }}
        >
          <Heading size="md" color="#3182CE" mb={6} display="flex" alignItems="center" position="relative" zIndex={1}>
            <Icon as={FaTrophy} mr={2} /> Bounty Pool
          </Heading>
          
          {/* Stats Boxes */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} spacing={4} mb={6} position="relative" zIndex={1}>
            {/* Overall RTP */}
            <Box 
              bg="rgba(20, 40, 80, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 128, 255, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaChartLine} 
                  color="#3182CE" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="blue.300" fontSize="sm" fontWeight="bold">Overall RTP</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">90%</Text>
                <Text color="#D946EF" fontSize="xs">75% MONETARY</Text>
                <Text color="#38BDF8" fontSize="xs">+15% BONUSES</Text>
              </VStack>
            </Box>
            
            {/* Prize Pool */}
            <Box 
              bg="rgba(20, 40, 80, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 128, 255, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaCoins} 
                  color="#FACC15" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="blue.300" fontSize="sm" fontWeight="bold">Prize Pool</Text>
                <Text color="white" fontSize="xl" fontWeight="bold" whiteSpace="nowrap">1,500,000 JINU</Text>
                <Text color="#FACC15" fontSize="xs">~$2,500 USD</Text>
              </VStack>
            </Box>
            
            {/* Cards Minted */}
            <Box 
              bg="rgba(20, 40, 80, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 128, 255, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaCoins} 
                  color="#38BDF8" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="blue.300" fontSize="sm" fontWeight="bold">Cards Minted</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">2,500</Text>
                <Text color="#38BDF8" fontSize="xs">7,500 remaining</Text>
              </VStack>
            </Box>
            
            {/* Prizes Claimed */}
            <Box 
              bg="rgba(20, 40, 80, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 128, 255, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaGift} 
                  color="#3182CE" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="blue.300" fontSize="sm" fontWeight="bold">Prizes Claimed</Text>
                <Text color="white" fontSize="xl" fontWeight="bold" whiteSpace="nowrap">375,000 JINU</Text>
                <Text color="#4ADE80" fontSize="xs">4,632 winners</Text>
              </VStack>
            </Box>
            
            {/* Top Prizes Left */}
            <Box 
              bg="rgba(20, 40, 80, 0.8)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
              textAlign="center"
              border="1px solid rgba(0, 128, 255, 0.2)"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={1}>
                <Icon 
                  as={FaTrophy} 
                  color="#FACC15" 
                  boxSize={6} 
                  mb={1}
                />
                <Text color="blue.300" fontSize="sm" fontWeight="bold">Top Prizes Left</Text>
                <Text color="white" fontSize="xl" fontWeight="bold">5</Text>
                <Text color="#FACC15" fontSize="xs">1X 100K JINU</Text>
                <Text color="#FACC15" fontSize="xs">4X 50K JINU</Text>
              </VStack>
            </Box>
          </SimpleGrid>
          
          {/* Progress Bars */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            {/* Minting Progress */}
            <Box 
              bg="rgba(0, 15, 30, 0.95)" 
              p={4} 
              borderRadius="lg"
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
              border="1px solid rgba(0, 128, 255, 0.15)"
              backdropFilter="blur(10px)"
            >
              <Text color="blue.300" fontSize="sm" fontWeight="bold" mb={2}>Minting Progress</Text>
              <Text color="white" fontSize="sm" mb={1}>2,500 minted</Text>
              <Progress 
                value={25} 
                colorScheme="blue" 
                size="sm" 
                bg="rgba(255, 255, 255, 0.1)" 
                borderRadius="full"
                mb={1}
              />
              <Flex justify="space-between">
                <Text color="gray.400" fontSize="xs">0</Text>
                <Text color="blue.300" fontSize="xs">10,000 total</Text>
              </Flex>
            </Box>
            
            {/* Prize Claims Progress */}
            <Box 
              bg="rgba(0, 15, 30, 0.95)" 
              p={4} 
              borderRadius="lg"
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
              border="1px solid rgba(0, 128, 255, 0.15)"
              backdropFilter="blur(10px)"
            >
              <Text color="blue.300" fontSize="sm" fontWeight="bold" mb={2}>Prize Claims Progress</Text>
              <Text color="white" fontSize="sm" mb={1}>375,000 JINU claimed</Text>
              <Progress 
                value={25} 
                colorScheme="green" 
                size="sm" 
                bg="rgba(255, 255, 255, 0.1)" 
                borderRadius="full"
                mb={1}
              />
              <Flex justify="space-between">
                <Text color="gray.400" fontSize="xs">0</Text>
                <Text color="green.300" fontSize="xs">1,500,000 JINU total</Text>
              </Flex>
            </Box>
          </SimpleGrid>
          
          {/* Prize Distribution and Recent Claims */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            {/* Prize Distribution Table */}
            <Box 
              bg="rgba(0, 15, 30, 0.95)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
              border="1px solid rgba(0, 128, 255, 0.15)"
              backdropFilter="blur(10px)"
            >
              <Heading size="sm" color="blue.300" mb={4}>Prize Distribution</Heading>
              <Table size="sm" variant="unstyled">
                <Thead>
                  <Tr>
                    <Th color="blue.200" fontSize="xs">PRIZE</Th>
                    <Th color="blue.200" fontSize="xs">TOTAL</Th>
                    <Th color="blue.200" fontSize="xs">CLAIMED</Th>
                    <Th color="blue.200" fontSize="xs">REMAIN</Th>
                    <Th color="blue.200" fontSize="xs">ODDS</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(124, 58, 237, 0.8) 0%, rgba(109, 40, 217, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">100,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">1</Td>
                    <Td color="white" fontSize="xs">0</Td>
                    <Td color="white" fontSize="xs">1</Td>
                    <Td color="white" fontSize="xs">1 in 10k</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(139, 92, 246, 0.8) 0%, rgba(124, 58, 237, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">50,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">2</Td>
                    <Td color="white" fontSize="xs">0</Td>
                    <Td color="white" fontSize="xs">2</Td>
                    <Td color="white" fontSize="xs">1 in 5k</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(168, 85, 247, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">25,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">4</Td>
                    <Td color="white" fontSize="xs">1</Td>
                    <Td color="white" fontSize="xs">3</Td>
                    <Td color="white" fontSize="xs">1 in 2.5k</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(192, 132, 252, 0.8) 0%, rgba(168, 85, 247, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">10,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">5</Td>
                    <Td color="white" fontSize="xs">1</Td>
                    <Td color="white" fontSize="xs">4</Td>
                    <Td color="white" fontSize="xs">1 in 2k</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(59, 130, 246, 0.8) 0%, rgba(37, 99, 235, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">5,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">10</Td>
                    <Td color="white" fontSize="xs">3</Td>
                    <Td color="white" fontSize="xs">7</Td>
                    <Td color="white" fontSize="xs">1 in 1k</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(14, 165, 233, 0.8) 0%, rgba(2, 132, 199, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">2,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">20</Td>
                    <Td color="white" fontSize="xs">6</Td>
                    <Td color="white" fontSize="xs">14</Td>
                    <Td color="white" fontSize="xs">1 in 500</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(34, 197, 94, 0.8) 0%, rgba(22, 163, 74, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">1,000 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">40</Td>
                    <Td color="white" fontSize="xs">12</Td>
                    <Td color="white" fontSize="xs">28</Td>
                    <Td color="white" fontSize="xs">1 in 250</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(132, 204, 22, 0.8) 0%, rgba(101, 163, 13, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">500 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">100</Td>
                    <Td color="white" fontSize="xs">30</Td>
                    <Td color="white" fontSize="xs">70</Td>
                    <Td color="white" fontSize="xs">1:100</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(250, 204, 21, 0.8) 0%, rgba(234, 179, 8, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">400 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">100</Td>
                    <Td color="white" fontSize="xs">25</Td>
                    <Td color="white" fontSize="xs">75</Td>
                    <Td color="white" fontSize="xs">1:100</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(249, 115, 22, 0.8) 0%, rgba(234, 88, 12, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">300 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">150</Td>
                    <Td color="white" fontSize="xs">40</Td>
                    <Td color="white" fontSize="xs">110</Td>
                    <Td color="white" fontSize="xs">1 in 66.7</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">200 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">3000</Td>
                    <Td color="white" fontSize="xs">600</Td>
                    <Td color="white" fontSize="xs">2400</Td>
                    <Td color="white" fontSize="xs">1 in 3.3</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(185, 28, 28, 0.8) 0%, rgba(153, 27, 27, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">100 JINU</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">1200</Td>
                    <Td color="white" fontSize="xs">250</Td>
                    <Td color="white" fontSize="xs">950</Td>
                    <Td color="white" fontSize="xs">1 in 8.3</Td>
                  </Tr>
                  
                  {/* Lottery Tickets Section - Non-winning cards */}
                  <Tr>
                    <Td colSpan={5} pt={4}>
                      <Text color="blue.300" fontWeight="bold" fontSize="sm">LOTTERY TICKETS</Text>
                      <Text color="blue.200" fontSize="xs" mt={1}>Non-winning cards receive lottery tickets for the Game Draw Wheel</Text>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(6, 182, 212, 0.8) 0%, rgba(14, 165, 233, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">5 Tickets</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">161</Td>
                    <Td color="white" fontSize="xs">-</Td>
                    <Td color="white" fontSize="xs">161</Td>
                    <Td color="white" fontSize="xs">3%</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(14, 165, 233, 0.8) 0%, rgba(2, 132, 199, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">4 Tickets</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">376</Td>
                    <Td color="white" fontSize="xs">-</Td>
                    <Td color="white" fontSize="xs">376</Td>
                    <Td color="white" fontSize="xs">7%</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(2, 132, 199, 0.8) 0%, rgba(3, 105, 161, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">3 Tickets</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">805</Td>
                    <Td color="white" fontSize="xs">-</Td>
                    <Td color="white" fontSize="xs">805</Td>
                    <Td color="white" fontSize="xs">15%</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(3, 105, 161, 0.8) 0%, rgba(7, 89, 133, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">2 Tickets</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">1342</Td>
                    <Td color="white" fontSize="xs">-</Td>
                    <Td color="white" fontSize="xs">1342</Td>
                    <Td color="white" fontSize="xs">25%</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <Box 
                        bg="linear-gradient(90deg, rgba(7, 89, 133, 0.8) 0%, rgba(12, 74, 110, 0.8) 100%)"
                        borderRadius="md"
                        py={1}
                        px={2}
                        display="inline-block"
                        boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                        border="0.75px solid rgba(255, 255, 255, 0.5)"
                        backdropFilter="blur(4px)"
                        minWidth="120px"
                        textAlign="center"
                      >
                        <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">1 Ticket</Text>
                      </Box>
                    </Td>
                    <Td color="white" fontSize="xs">2684</Td>
                    <Td color="white" fontSize="xs">-</Td>
                    <Td color="white" fontSize="xs">2684</Td>
                    <Td color="white" fontSize="xs">50%</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            
            {/* Recent Claims */}
            <Box 
              bg="rgba(0, 15, 30, 0.95)" 
              p={4} 
              borderRadius="lg" 
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
              border="1px solid rgba(0, 128, 255, 0.15)"
              backdropFilter="blur(10px)"
            >
              <Heading size="sm" color="blue.300" mb={4}>Recent Claims</Heading>
              <Table size="sm" variant="unstyled">
                <Thead>
                  <Tr>
                    <Th color="blue.200" fontSize="xs">PLAYER</Th>
                    <Th color="blue.200" fontSize="xs">PRIZE</Th>
                    <Th color="blue.200" fontSize="xs">TIME</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {getBountyPoolData().recentWinners.slice(0, 5).map((winner, index) => (
                    <Tr key={index}>
                      <Td>
                        <Box 
                          bg="rgba(168, 85, 247, 0.2)" 
                          borderRadius="sm" 
                          py={0.5} 
                          px={2} 
                          display="inline-block"
                          border="1px solid rgba(168, 85, 247, 0.2)"
                        >
                          <Text color="purple.300" fontFamily="monospace" fontSize="xs">{winner.player}</Text>
                        </Box>
                      </Td>
                      <Td>
                        <Box 
                          bg={winner.amount >= 10000 ? "linear-gradient(90deg, rgba(59, 130, 246, 0.8) 0%, rgba(37, 99, 235, 0.8) 100%)" :
                              winner.amount >= 1000 ? "linear-gradient(90deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)" :
                              winner.amount === 0 ? "linear-gradient(90deg, rgba(249, 115, 22, 0.8) 0%, rgba(234, 88, 12, 0.8) 100%)" :
                              "linear-gradient(90deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)"}
                          borderRadius="md" 
                          py={1} 
                          px={3} 
                          display="inline-block"
                          boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                          border="0.75px solid rgba(255, 255, 255, 0.5)"
                          backdropFilter="blur(4px)"
                          minWidth="100px"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          <Text color="white" fontWeight="extrabold" fontSize="xs" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">
                            {winner.amount === 0 ? 'GIFT BOX' : `${winner.amount.toLocaleString()} JINU`}
                          </Text>
                        </Box>
                      </Td>
                      <Td>
                        <Text color="gray.400">{Math.floor((Date.now() - winner.timestamp) / (1000 * 60))} minutes ago</Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            
          </SimpleGrid>
          
          {/* Prize Summary Section */}
          <Box 
            bg="rgba(0, 15, 30, 0.85)" 
            p={4} 
            borderRadius="lg" 
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(0, 128, 255, 0.15)"
            backdropFilter="blur(10px)"
            mb={6}
          >
            <Heading size="md" color="blue.300" mb={4} textAlign="center">Prize Summary</Heading>
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              {/* Top Prizes */}
              <Box 
                bg="rgba(0, 15, 30, 0.95)" 
                p={4} 
                borderRadius="lg" 
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
                border="1px solid rgba(0, 128, 255, 0.15)"
                backdropFilter="blur(10px)"
                textAlign="center"
              >
                <Heading size="sm" color="yellow.300" mb={2}>Top Prizes</Heading>
                <Text color="white" fontWeight="bold" fontSize="xl">12 cards</Text>
                <Text color="yellow.300" fontSize="sm">350,000 JINU</Text>
              </Box>

              {/* Mid-tier Prizes */}
              <Box 
                bg="rgba(0, 15, 30, 0.95)" 
                p={4} 
                borderRadius="lg" 
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
                border="1px solid rgba(0, 128, 255, 0.15)"
                backdropFilter="blur(10px)"
                textAlign="center"
              >
                <Heading size="sm" color="blue.300" mb={2}>Mid-tier Prizes</Heading>
                <Text color="white" fontWeight="bold" fontSize="xl">420 cards</Text>
                <Text color="blue.300" fontSize="sm">265,000 JINU</Text>
              </Box>

              {/* Lower Prizes */}
              <Box 
                bg="rgba(0, 15, 30, 0.95)" 
                p={4} 
                borderRadius="lg" 
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
                border="1px solid rgba(0, 128, 255, 0.15)"
                backdropFilter="blur(10px)"
                textAlign="center"
              >
                <Heading size="sm" color="green.300" mb={2}>Lower Prizes</Heading>
                <Text color="white" fontWeight="bold" fontSize="xl">4,200 cards</Text>
                <Text color="green.300" fontSize="sm">720,000 JINU</Text>
              </Box>

              {/* Win Odds */}
              <Box 
                bg="rgba(0, 15, 30, 0.95)" 
                p={4} 
                borderRadius="lg" 
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
                border="1px solid rgba(0, 128, 255, 0.15)"
                backdropFilter="blur(10px)"
                textAlign="center"
              >
                <Heading size="sm" color="blue.300" mb={2}>Win Odds</Heading>
                <Text color="white" fontWeight="bold" fontSize="xl">1 in 2.15</Text>
                <Text color="blue.300" fontSize="sm">Top Prize: 1 in 10,000</Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Second Chance Fortune Wheel */}
          <Box 
            bg="rgba(0, 15, 30, 0.8)" 
            p={6} 
            borderRadius="lg" 
            border="1px solid rgba(0, 128, 255, 0.3)"
            backdropFilter="blur(10px)"
            mb={6}
          >
            <Heading size="md" color="blue.300" mb={4} textAlign="center">Game Draw Wheel</Heading>
            <Text fontSize="sm" color="blue.100" mb={4} textAlign="center">
              Non-winning cards earn you lottery tickets for this wheel! The wheel spins automatically when the game fills.
            </Text>
            
            {/* Stats display above tabs */}
            <HStack spacing={4} justify="center" mb={4}>
              <Stat textAlign="center" bg="rgba(0, 30, 60, 0.7)" p={3} borderRadius="md" border="1px solid rgba(0, 128, 255, 0.3)">
                <StatLabel color="blue.300">Your Tickets</StatLabel>
                <StatNumber color="white" fontSize="2xl">{gameState && gameState.lotteryTickets ? gameState.lotteryTickets : 0}</StatNumber>
                <StatHelpText color="blue.200">From non-winning cards</StatHelpText>
              </Stat>
              
              <Stat textAlign="center" bg="rgba(0, 30, 60, 0.7)" p={3} borderRadius="md" border="1px solid rgba(0, 128, 255, 0.3)">
                <StatLabel color="blue.300">Your Chance</StatLabel>
                <StatNumber color="white" fontSize="2xl">{gameState && gameState.winProbability ? gameState.winProbability.toFixed(1) : 0}%</StatNumber>
                <StatHelpText color="blue.200">Win probability</StatHelpText>
              </Stat>
            </HStack>
            
            {/* Game status bar */}
            <Box bg="rgba(0, 30, 60, 0.7)" p={3} borderRadius="md" border="1px solid rgba(0, 128, 255, 0.3)" mb={4}>
              <Flex justify="space-between" align="center">
                <Text color="blue.300" fontSize="sm">
                  <Icon as={FaInfoCircle} mr={1} />
                  Game #{gameState && gameState.currentBatch ? gameState.currentBatch : 42} - Prize: 25,000 JINU
                </Text>
                <HStack>
                  <Progress 
                    value={gameState && gameState.gamesPlayed ? gameState.gamesPlayed : 87} 
                    max={gameState && gameState.batchSize ? gameState.batchSize : 100} 
                    size="xs" 
                    colorScheme="blue" 
                    width="80px" 
                    borderRadius="full" 
                  />
                  <Text color="blue.200" fontSize="xs">
                    {gameState && gameState.gamesPlayed ? gameState.gamesPlayed : 87}/
                    {gameState && gameState.batchSize ? gameState.batchSize : 100}
                  </Text>
                  <Badge colorScheme="blue" variant="solid" fontSize="xs">
                    Auto-spins at {gameState && gameState.batchSize ? gameState.batchSize : 100} cards
                  </Badge>
                </HStack>
              </Flex>
            </Box>
            
            {/* Tabs for Game Wheel, Batch Info, and Documentation */}
            <Tabs variant="soft-rounded" colorScheme="blue" size="sm">
              <TabList justifyContent="center">
                <Tab>Wheel</Tab>
                <Tab>Batch Info</Tab>
                <Tab>How It Works</Tab>
              </TabList>
              
              <TabPanels mt={4}>
                {/* Tab 1: Game Wheel */}
                <TabPanel p={0}>
                  <Box 
                    bg="rgba(0, 30, 60, 0.7)" 
                    p={6} 
                    borderRadius="md" 
                    border="1px solid rgba(0, 128, 255, 0.3)"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <ReusableFortuneWheel 
                      config={{
                        title: "", // Empty title since we're using the container's heading
                        description: "", // Empty description since we're using the container's text
                        titleGradient: "linear(to-r, blue.400, cyan.400)",
                        titleFontSize: "2xl",
                        descriptionColor: "blue.100",
                        spinDuration: 5000,
                        spinRotations: 1800, // More rotations for dramatic effect
                        showSegmentsInfo: false, // Hide segment info since we'll show it separately
                        showTimer: false,
                        showControls: false, // Hide all controls
                        prizes: { min: 5000, max: 25000, currency: "JINU" },
                        segments: FortuneWheelIntegration.generateWheelSegmentsForQuickCash(
                          gameState && gameState.currentBatch ? gameState.currentBatch.toString() : "1",
                          "user_" + Math.random().toString(36).substring(2, 9) // Mock user ID - replace with actual user ID in production
                        )
                      }}
                      // Auto-spin if game is nearly full (for demo purposes)
                      autoSpin={gameState && gameState.demoAutoSpin ? gameState.demoAutoSpin : false}
                      // Game information - dynamically set based on game configuration
                      batchInfo={{
                        current: gameState && gameState.gamesPlayed ? gameState.gamesPlayed : 87, // Use actual games played or default to 87 for demo
                        total: gameState && gameState.batchSize ? gameState.batchSize : 100, // Use configured batch size or default to 100
                        batchNumber: gameState && gameState.currentBatch ? gameState.currentBatch : 42 // Use current batch number or default to 42
                      }}
                      size={500} /* Wheel size */
                      onWin={(winData) => {
                        // Add the win amount to the player's balance if they won
                        if (winData.isPlayerWin) {
                          toast({
                            title: "Game Draw Win!",
                            description: `You won ${winData.amount} ${winData.currency} from the Game Draw Wheel!`,
                            status: "success",
                            duration: 5000,
                            isClosable: true,
                            position: "top"
                          });
                        } else {
                          toast({
                            title: "Better Luck Next Time",
                            description: `${winData.segment.name} won this round. Keep collecting tickets for the next game!`,
                            status: "info",
                            duration: 5000,
                            isClosable: true,
                            position: "top"
                          });
                        }
                      }}
                      theme="default"
                    />
                  </Box>
                </TabPanel>
                
                {/* Tab 2: Batch Info */}
                <TabPanel p={0}>
                  <Box 
                    bg="rgba(0, 30, 60, 0.7)" 
                    p={6} 
                    borderRadius="md" 
                    border="1px solid rgba(0, 128, 255, 0.3)"
                  >
                    <Heading size="sm" color="blue.300" mb={4}>Batch Statistics</Heading>
                    <SimpleGrid columns={2} spacing={4} mb={4}>
                      <Stat textAlign="center" bg="rgba(0, 20, 40, 0.7)" p={3} borderRadius="md" border="1px solid rgba(0, 128, 255, 0.3)">
                        <StatLabel color="blue.300">Cards Minted</StatLabel>
                        <StatNumber color="white">{gameState && gameState.gamesPlayed ? gameState.gamesPlayed : 87}/100</StatNumber>
                        <StatHelpText color="blue.200">87% Complete</StatHelpText>
                      </Stat>
                      <Stat textAlign="center" bg="rgba(0, 20, 40, 0.7)" p={3} borderRadius="md" border="1px solid rgba(0, 128, 255, 0.3)">
                        <StatLabel color="blue.300">Cards Revealed</StatLabel>
                        <StatNumber color="white">{gameState && gameState.gamesRevealed ? gameState.gamesRevealed : 75}/100</StatNumber>
                        <StatHelpText color="blue.200">75% Revealed</StatHelpText>
                      </Stat>
                    </SimpleGrid>
                    
                    <Heading size="sm" color="blue.300" mb={4}>Prize Distribution</Heading>
                    <Table variant="simple" size="sm" mb={4}>
                      <Thead>
                        <Tr>
                          <Th color="blue.300">Prize</Th>
                          <Th color="blue.300">Count</Th>
                          <Th color="blue.300">Odds</Th>
                          <Th color="blue.300">Claimed</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td color="white">25,000 JINU</Td>
                          <Td color="white">1</Td>
                          <Td color="white">1:100</Td>
                          <Td color="white">0/1</Td>
                        </Tr>
                        <Tr>
                          <Td color="white">10,000 JINU</Td>
                          <Td color="white">5</Td>
                          <Td color="white">1:20</Td>
                          <Td color="white">2/5</Td>
                        </Tr>
                        <Tr>
                          <Td color="white">5,000 JINU</Td>
                          <Td color="white">10</Td>
                          <Td color="white">1:10</Td>
                          <Td color="white">5/10</Td>
                        </Tr>
                        <Tr>
                          <Td color="white">1,000 JINU</Td>
                          <Td color="white">20</Td>
                          <Td color="white">1:5</Td>
                          <Td color="white">12/20</Td>
                        </Tr>
                      </Tbody>
                    </Table>
                    
                    <Heading size="sm" color="blue.300" mb={4}>Lottery Tickets</Heading>
                    <Text color="white" mb={3}>Non-winning cards receive lottery tickets for the Game Draw Wheel:</Text>
                    
                    {/* Get batch statistics */}
                    {(() => {
                      // Get the latest batch statistics
                      const batchStats = QuickCashVirtualDeck.getBatchStatistics();
                      console.log('Batch statistics for lottery tickets:', batchStats);
                      
                      // Make sure we have a current batch, if not create one
                      if (!batchStats) {
                        console.log('No batch found, creating a new one');
                        QuickCashVirtualDeck.createNewBatch(100, 10);
                      }
                      
                      // Get the statistics again if needed
                      const updatedStats = batchStats || QuickCashVirtualDeck.getBatchStatistics();
                      const lotteryStats = updatedStats?.lotteryTickets || { distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, total: 0 };
                      console.log('Lottery stats:', lotteryStats);
                      
                      return (
                        <>
                          <Table variant="simple" size="sm" mb={4}>
                            <Thead>
                              <Tr>
                                <Th color="blue.300">Tickets</Th>
                                <Th color="blue.300">Probability</Th>
                                <Th color="blue.300">Cards</Th>
                                <Th color="blue.300">Drawn</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              <Tr>
                                <Td color="white">1 Ticket</Td>
                                <Td color="white">50%</Td>
                                <Td color="white">~2,684 cards</Td>
                                <Td color="white">{lotteryStats.distribution[1] || 0}</Td>
                              </Tr>
                              <Tr>
                                <Td color="white">2 Tickets</Td>
                                <Td color="white">25%</Td>
                                <Td color="white">~1,342 cards</Td>
                                <Td color="white">{lotteryStats.distribution[2] || 0}</Td>
                              </Tr>
                              <Tr>
                                <Td color="white">3 Tickets</Td>
                                <Td color="white">15%</Td>
                                <Td color="white">~805 cards</Td>
                                <Td color="white">{lotteryStats.distribution[3] || 0}</Td>
                              </Tr>
                              <Tr>
                                <Td color="white">4 Tickets</Td>
                                <Td color="white">7%</Td>
                                <Td color="white">~376 cards</Td>
                                <Td color="white">{lotteryStats.distribution[4] || 0}</Td>
                              </Tr>
                              <Tr>
                                <Td color="white">5 Tickets</Td>
                                <Td color="white">3%</Td>
                                <Td color="white">~161 cards</Td>
                                <Td color="white">{lotteryStats.distribution[5] || 0}</Td>
                              </Tr>
                            </Tbody>
                          </Table>
                          
                          <Box bg="rgba(0, 20, 40, 0.7)" p={3} borderRadius="md" border="1px solid rgba(0, 128, 255, 0.3)" mb={4}>
                            <Text color="white" fontWeight="bold">Total Lottery Tickets: {lotteryStats.total || 0}</Text>
                            <Text color="white">Average Tickets Per Non-Winning Card: {lotteryStats.averagePerCard || '0'}</Text>
                          </Box>
                        </>
                      );
                    })()}
                    
                  </Box>
                </TabPanel>
                
                {/* Tab 3: How It Works */}
                <TabPanel p={0}>
                  <Box 
                    bg="rgba(0, 30, 60, 0.7)" 
                    p={6} 
                    borderRadius="md" 
                    border="1px solid rgba(0, 128, 255, 0.3)"
                  >
                    <Heading size="sm" color="blue.300" mb={4}>How the Game Draw Works</Heading>
                    <Text color="white" mb={4}>
                      The Game Draw Wheel is a second chance drawing for all players who participated in this batch of Quick Cash cards.
                    </Text>
                    
                    <OrderedList color="white" spacing={2} pl={4} mb={4}>
                      <ListItem>Every non-winning card earns you 1 lottery ticket for the Game Draw.</ListItem>
                      <ListItem>Your chance of winning is proportional to how many tickets you have compared to all players.</ListItem>
                      <ListItem>The wheel automatically spins when all 100 cards in the batch have been minted.</ListItem>
                      <ListItem>The winner receives 25,000 JINU tokens as a consolation prize.</ListItem>
                    </OrderedList>
                    
                    <Heading size="sm" color="blue.300" mb={4}>Fairness & Transparency</Heading>
                    <Text color="white" mb={4}>
                      The Game Draw uses the same provably fair random number generation as the main Quick Cash game, ensuring that the selection process is completely transparent and verifiable on-chain.
                    </Text>
                    
                    <Code p={4} borderRadius="md" bg="rgba(0, 20, 40, 0.7)" color="blue.100" mb={4}>
                      {`// Simplified pseudocode for the Game Draw selection
function selectWinner(tickets) {
  const randomSeed = generateProvablyFairSeed();
  const totalTickets = tickets.reduce((sum, t) => sum + t.count, 0);
  const winningTicket = randomSeed % totalTickets;
  return determineWinnerFromTicket(winningTicket, tickets);
}`}
                    </Code>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Box>
      
      {/* Win Modal - Glassmorphism style with auto-dismiss */}
      <Modal isOpen={isWinOpen} onClose={onWinClose} isCentered motionPreset="scale">
        <ModalOverlay 
          bg="rgba(0, 0, 0, 0.4)"
          backdropFilter="blur(8px)"
        />
        <ModalContent
          bg="rgba(20, 30, 70, 0.7)"
          borderRadius="xl"
          boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.37)"
          backdropFilter="blur(8px)"
          border="1px solid rgba(255, 255, 255, 0.18)"
          p={4}
          maxW="sm"
          overflow="hidden"
          position="relative"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 0
          }}
        >
          {/* Confetti particles */}
          <Box 
            position="absolute" 
            top="0" 
            left="0" 
            right="0" 
            bottom="0"
            overflow="hidden"
            zIndex="1"
            pointerEvents="none"
          >
            {Array.from({ length: 15 }).map((_, i) => {
              // Pre-calculate random values
              const leftPos = Math.random() * 100;
              const size = Math.random() * 8 + 4;
              const delay = Math.random() * 1.5;
              const duration = 2 + Math.random() * 2;
              const colors = ['yellow.400', 'blue.400', 'green.400', 'purple.400', 'pink.400'];
              const color = colors[Math.floor(Math.random() * colors.length)];
              
              return (
                <Box
                  key={i}
                  position="absolute"
                  top="0"
                  left={`${leftPos}%`}
                  width={`${size}px`}
                  height={`${size}px`}
                  bg={color}
                  borderRadius="full"
                  boxShadow={`0 0 ${Math.floor(size/2)}px ${color}`}
                  opacity={0.8}
                  animation={`${confettiFall} ${duration}s ease-in ${delay}s forwards`}
                />
              );
            })}
          </Box>
          
          {/* Glow effect behind the modal */}
          <Box
            position="absolute"
            top="-10px"
            left="-10px"
            right="-10px"
            bottom="-10px"
            borderRadius="xl"
            zIndex="0"
            bgGradient="radial(circle at 50% 50%, rgba(255,215,0,0.15), transparent 70%)"
          />
          
          <Heading 
            textAlign="center" 
            fontSize="2xl" 
            mb={4} 
            color="yellow.300"
            textShadow="0 0 10px rgba(255,215,0,0.5)"
            zIndex="2"
            position="relative"
          >
            🎉 Congratulations! 🎉
          </Heading>
          
          <VStack spacing={4} position="relative" zIndex="2">
            {gameResult?.matchedValue && (
              <Text 
                fontSize="xl" 
                fontWeight="bold" 
                textAlign="center" 
                color="yellow.300"
                textShadow="0 0 8px rgba(255,215,0,0.4)"
              >
                You matched 3x {gameResult.matchedValue}!
              </Text>
            )}
            <Text 
              fontSize="2xl" 
              fontWeight="bold" 
              textAlign="center"
              bgGradient="linear(to-r, yellow.300, yellow.500, yellow.300)"
              bgClip="text"
              bgSize="200% 100%"
              textShadow="0 0 10px rgba(255,215,0,0.3)"
              animation={`${shimmer} 2s linear infinite`}
            >
              You won {gameResult?.amount ? gameResult.amount.toLocaleString() : 0} JINU!
            </Text>
            <Text 
              textAlign="center"
              color="white"
              fontSize="md"
              opacity="0.9"
            >
              Your winnings have been added to your balance.
            </Text>
          </VStack>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default QuickCashGame;
